import { traceDiscoveryMapping } from '#~/device/add-trace.js';
/* eslint-disable jsdoc/require-jsdoc */
import { ensureNotNil } from '@studio/js-utils';
import { normalizeId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import type { Configuration as OnvifConfig } from '@studio/models/control/onvif';
import type { Configuration as ViscaIpConfig } from '@studio/models/control/visca-ip';
import type { Model } from '@studio/models/device';
import { DeviceDefaults, isTransient, Limits } from '@studio/models/device';
import {
  OptionsNDI,
  QualityNDI,
  StatusDiscovery,
  StatusPowerState,
  StatusStreamState,
  VideoCodec
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import { toNumber, toString } from '@tryiris-ai/js-types';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { pick } from 'lodash-es';
import {
  jsToPbStudioDeviceAudio,
  pbToJsAudio,
  pbToJsAudioTemplate,
  pbToJsDiscoveryAudio
} from './audio.js';
import {
  jsToPbStudioDeviceAutoTracking,
  jsToPbStudioDeviceControl,
  pbToJsAutoTracking,
  pbToJsControl,
  pbToJsControlTemplate,
  pbToJsDiscoveryControl
} from './control.js';
import { pbToJsDeviceSettings } from './settings.js';
import type {
  DeviceOptions,
  PbControl,
  PbControlDevice,
  PbState,
  PbStudioDevice
} from './types.js';
import {
  jsToPbStudioDeviceVideo,
  pbToJsDiscoveryVideo,
  pbToJsVideo,
  pbToJsVideoTemplate
} from './video.js';
// import { broker } from '#~/agent/index.js';
import { toModel } from '#~/api/device/registry/index.js';
import type { Registrant } from '#~/api/device/registry/index.js';
import { toHexFromId, toIdFromHex } from '#~/api/index.js';
import type { Discovery } from '#~/device/discovery/index.js';
import type { Device } from '#~/device/index.js';
import { toConfiguration, toUrx } from '#~/device/index.js';
import type { Template } from '#~/device/registry/index.js';
import { logger } from '#~/logger/index.js';
import type { Configuration as RtspConfig } from '@studio/models/video/rtsp';
import { Protocol } from '@studio/models/video';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import { DefaultBitrate } from '#~/video/constants.js';

/* JS => PB */

const jsToPbControlDevice = (
  device: Device,
  options: DeviceOptions,
  _embedded?: boolean
): T_Maybe<PbControlDevice> => {
  const config = toConfiguration(device);

  const { liveKit } = options;

  let urx = toUrx(config, {
    isTransient: isTransient(device.id),
    liveKit,
    ...pick(device, ['modelId'])
  });

  if (/* embedded && */ urx && config.control !== undefined) {
    const urxItems = urx.split(',');

    if (urxItems.findIndex((item) => item.startsWith('http')) > -1) {
      return urx ? { urx } : undefined;
    }

    const user =
      (<T_Maybe<OnvifConfig>>config.control)?.credentials?.username ??
      (<T_Maybe<RtspConfig>>config.video)?.credentials?.username;
    const pass =
      (<T_Maybe<OnvifConfig>>config.control)?.credentials?.password ??
      (<T_Maybe<RtspConfig>>config.video)?.credentials?.password;

    const auth = user && pass ? `${user}:${pass}@` : '';

    const httpItem = `http://${auth}${(<ViscaIpConfig>config.control).host}:0`;

    const viscaIndex = urxItems.findIndex((item) => item.startsWith('visca'));
    if (viscaIndex > -1) {
      urxItems.splice(viscaIndex, 0, httpItem);
    } else {
      urxItems.push(httpItem);
    }

    urx = urxItems.join(',');
  }

  var ndi: T_Maybe<DeepPartial<OptionsNDI>> = {};

  if (device.video?.config?.protocol === Protocol.NDI) {
    ndi = {
      maxBitrate:
        Number.parseInt((device.video?.config?.query?.ndiBitrate as string) ?? DefaultBitrate, 10) *
        8,
      maxFps: Number.parseInt((device.video?.config?.query?.ndiFramerate as string) ?? 30, 10),
      videoCodec: VideoCodec.VP8,
      quality: Number.parseInt(
        (device.video?.config?.query?.ndiBandwidthMode as string) ?? QualityNDI.HIGH,
        10
      ),
      frameSync:
        Number.parseInt((device.video?.config?.query?.ndiFrameSyncMode as string) ?? 1, 10) === 1
    };
  }

  return urx ? { urx, ndi } : undefined;
};

const jsToPbStudioDevice = (device: Device, options: DeviceOptions): T_Maybe<PbStudioDevice> => {
  const audio = jsToPbStudioDeviceAudio(device, options);
  const autoTrackingControl = jsToPbStudioDeviceAutoTracking(device);
  const control = jsToPbStudioDeviceControl(device);
  const model = toIdFromHex(device.modelId);
  const video = jsToPbStudioDeviceVideo(device, options);
  const name = device.name.slice(0, Limits.maxNameLength);
  const { rank } = device;

  if (!model || !name || (!audio && !control && !video)) {
    return undefined;
  }

  return {
    addedAt: Date.now() / 1000,
    audio,
    autoTrackingControl,
    control,
    model,
    name,
    rank,
    video,
    // ENG-4832: how the camera was identified (StudioDevice fields 13–15). Written only when
    // recorded, so devices without them serialize exactly as before.
    // Per rule: wire-contract-changes-must-not-break-old-firmware (ai.knowledge/global) — additive
    // optional fields; peers that predate them skip the unknown tags.
    ...(device.typedBrand ? { typedBrand: device.typedBrand } : {}),
    ...(device.typedModel ? { typedModel: device.typedModel } : {}),
    ...(device.identifiedBy ? { identifiedBy: device.identifiedBy } : {})
  };
};

export const jsToPbControl = (
  device: Device,
  options: DeviceOptions,
  embedded?: boolean
): T_Maybe<PbControl> => {
  const pbControl = jsToPbControlDevice(device, options, embedded);
  const pbStudio = jsToPbStudioDevice(device, options);

  if (!pbControl || !pbStudio) {
    return undefined;
  }

  const { id } = device;

  return {
    device: {
      [id]: pbControl
    },
    studio: {
      autoTrackingReservation: {
        // Ignored
      },
      device: {
        [id]: pbStudio
      }
    }
  };
};

/* PB => JS */

export const pbToJsDevice = (
  agentId: Agent['id'],
  key: string,
  pbState: T_Optional<PbState>,
  embedded: boolean
): T_Maybe<Device> => {
  const device = ((): T_Maybe<Device> => {
    const id = normalizeId<Device['id']>(key);

    if (!id) {
      return undefined;
    }

    // Device information is stored in three separate trees - control, studio and status.
    const pbControl = pbState.control?.device?.[id] ?? {};
    const pbStudio = pbState.control?.studio?.device?.[id] ?? {};
    const pbStatus = pbState.status?.device?.[id] ?? {};

    const pbDevice = { pbControl, pbStatus, pbStudio };

    const audio = pbToJsAudio(pbDevice);
    const autoTracking = pbToJsAutoTracking(pbStudio.autoTrackingControl);
    const control = pbToJsControl(pbDevice);
    const modelId = toHexFromId<Model['id']>(pbStudio.model);
    const name = toString(pbStudio.name)?.slice(0, Limits.maxNameLength);
    const rank = toNumber(pbStudio.rank) ?? DeviceDefaults.rank;
    const settings = pbToJsDeviceSettings(pbDevice);
    const video = pbToJsVideo(pbDevice);

    if (!modelId || !name || (!audio && !control && !video)) {
      return undefined;
    }

    let powerState = pbStatus?.config?.power?.state ?? StatusPowerState.POWER_STATE_UNKNOWN;

    const videoState = pbStatus?.video?.status?.state ?? StatusStreamState.STREAM_STATE_UNKNOWN;

    const deviceAddedAt = pbState.control?.studio?.device?.[id]?.addedAt ?? 0;

    const deviceAddedElapsed = Date.now() / 1000 - deviceAddedAt;

    const deviceAddedTimeout =
      videoState === StatusStreamState.STREAM_STATE_FAIL ||
      videoState === StatusStreamState.STREAM_STATE_NO_PREROLL
        ? 5
        : 30;

    if (
      powerState === StatusPowerState.POWER_STATE_UNKNOWN &&
      deviceAddedElapsed > deviceAddedTimeout
    ) {
      powerState = video?.hasConnected
        ? StatusPowerState.POWER_STATE_ON
        : StatusPowerState.POWER_STATE_STANDBY;
    }

    const agentStatus =
      pbState.status?.encoder !== undefined ? 'online' : embedded ? 'offline-embedded' : 'offline';

    // Per-aspect authentication verdicts produced by the encoder's
    // driver stack. Each aspect carries its own field — the studio
    // aggregates as needed (e.g. "any aspect INVALID → show creds-bad
    // badge"). See AuthenticationStatus enum and the proto definition
    // of StatusDeviceVideo / StatusDeviceMovement for semantics.
    //
    // The "movement" aspect represents the control surface (HTTP,
    // ONVIF, VISCA) — the proto field name is historical; we expose
    // it as `controlAuthenticationStatus` on the studio-side Device
    // type for clarity. Future schema cleanup may rename movement →
    // control upstream.
    const videoAuthenticationStatus = pbStatus?.video?.authenticationStatus;
    const controlAuthenticationStatus = pbStatus?.movement?.authenticationStatus;

    return {
      agentId,
      agentStatus,
      audio,
      videoAuthenticationStatus,
      controlAuthenticationStatus,
      autoTracking,
      control,
      id,
      ...(deviceAddedAt > 0 ? { addedAt: deviceAddedAt } : {}),
      modelId,
      name,
      powerState,
      rank,
      settings,
      video,
      // ENG-4832: read back so a restore (which rebuilds the record from this device) keeps them.
      ...(pbStudio.typedBrand ? { typedBrand: pbStudio.typedBrand } : {}),
      ...(pbStudio.typedModel ? { typedModel: pbStudio.typedModel } : {}),
      ...(pbStudio.identifiedBy ? { identifiedBy: pbStudio.identifiedBy } : {})
    };
  })();

  if (!device) {
    logger.warning(`Unable to deserialize device: ${key}`);
  }

  return device;
};

export const pbToJsDiscovery = (
  agentId: Agent['id'],
  key: Discovery['id'],
  pbDiscovery: StatusDiscovery
): T_Maybe<Discovery> => {
  const discovery = ((): T_Maybe<Discovery> => {
    const id = normalizeId<Discovery['id']>(key);

    if (!id) {
      return undefined;
    }

    if (!pbDiscovery) {
      return undefined;
    }

    // Do we have a video device?
    const video = pbToJsDiscoveryVideo(agentId, pbDiscovery);

    if (video) {
      return { id, ...video };
    }

    // Perhaps a control device (ONVIF)?
    const control = pbToJsDiscoveryControl(agentId, pbDiscovery);

    if (control) {
      return { id, ...control };
    }

    // Then maybe an audio device?
    const audio = pbToJsDiscoveryAudio(agentId, pbDiscovery);

    return audio ? { id, ...audio } : undefined;
  })();

  if (!discovery) {
    logger.warning(`Unable to deserialize discovery device: ${key}`);
  }

  traceDiscoveryMapping(agentId, key, pbDiscovery, discovery);
  return discovery;
};

export const pbToJsRegistryTemplate = (registrant: Registrant): Template => ({
  audio: pbToJsAudioTemplate(registrant),
  control: pbToJsControlTemplate(registrant),
  model: ensureNotNil(toModel(registrant.model)),
  video: pbToJsVideoTemplate(registrant)
});
